import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PermissionCode } from '@solar/shared';
import { api, ApiError, UNAUTHORIZED_EVENT } from '@/lib/api';

export interface Me {
  id: string;
  username: string;
  fullName: string;
  email: string | null;
  title: string | null;
  phone: string | null;
  officeRegionId: string | null;
  officeRegion: {
    id: string;
    name: string;
    latitude: number | null;
    longitude: number | null;
  } | null;
  mustChangePassword: boolean;
  roles: { id: string; code: string; name: string }[];
  teamId: string | null;
  managedTeamIds: string[];
  permissions: PermissionCode[];
}

interface AuthCtx {
  user: Me | null;
  loading: boolean;
  can: (...codes: PermissionCode[]) => boolean;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<unknown>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const me = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api.get<Me>('/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  useEffect(() => {
    const onUnauth = () => {
      if (qc.getQueryData(['me'])) {
        qc.setQueryData(['me'], null);
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      }
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauth);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauth);
  }, [qc]);

  const login = useCallback(
    async (login: string, password: string) => {
      await api.post('/auth/login', { login, password });
      // Không dùng qc.clear(): observer của query 'me' phải nhận dữ liệu mới
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
      qc.setQueryData(['me'], await api.get<Me>('/auth/me'));
    },
    [qc],
  );
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      qc.setQueryData(['me'], null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
    }
  }, [qc]);

  const value = useMemo<AuthCtx>(() => {
    const user = me.data ?? null;
    const perms = new Set(user?.permissions ?? []);
    return {
      user,
      loading: me.isLoading,
      can: (...codes) => codes.some((c) => perms.has(c)),
      login,
      logout,
      refresh: () => me.refetch(),
    };
  }, [me, login, logout]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAuth phải dùng trong AuthProvider');
  return c;
}
